import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { citationIndex, sourceState } from '../chat/trust';

/**
 * An inline citation marker (design spec §9.1 steps 5–6).
 *
 * Rendered in place of the `[S1]` marker the model wrote into the answer. It is
 * a real button, so it is keyboard reachable and announced with a name that
 * says which source it points at — the number alone would be meaningless to a
 * screen reader, and colour alone would be meaningless to everyone (§12).
 *
 * A marker whose descriptor never arrived is NOT dropped: it renders in the
 * `unreachable` state with the "Quelle nicht erreichbar" name, because a claim
 * pointing at nothing is information the reader needs (§11.2).
 *
 * @param {object} props
 * @param {string} props.sourceId - e.g. 's1' (lower-case id from the wire).
 * @param {object} [props.source] - the resolved descriptor, when we have one.
 * @param {number} [props.index] - display number; derived from the id when omitted.
 * @param {boolean} [props.active] - true while the SourcePanel focuses this source.
 * @param {(sourceId: string) => void} [props.onOpen]
 */
export const Citation = forwardRef(function Citation(
  { sourceId, source, index, active = false, onOpen, className = '', ...rest },
  ref
) {
  const { t } = useTranslation();
  const id = String(sourceId ?? source?.id ?? '').toLowerCase();
  const number = index ?? citationIndex(id);
  const state = source ? sourceState(source) : 'unreachable';
  const title = source?.title || null;

  const label = state === 'unreachable' && !title
    ? t('chat.citationUnreachableLabel', { index: number })
    : t('chat.citationLabel', { index: number, title: title || t('states.noSource') });

  return (
    <button
      ref={ref}
      type="button"
      className={`etch-citation ${className}`.trim()}
      data-state={state}
      data-active={active || undefined}
      aria-label={label}
      onClick={() => onOpen?.(id)}
      {...rest}
    >
      <span className="etch-citation__marker" aria-hidden="true">
        {number ?? '?'}
      </span>
    </button>
  );
});

export default Citation;
